package com.dotdrives.web

/** Organization paths are workspace identity, never an authorization grant. */
private val orgRouteUuid = Regex("[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}")

data class OrganizationRoute(val orgId: String?, val resourceHash: String, val invalid: Boolean = false)

fun parseOrganizationRoute(hash: String): OrganizationRoute {
    val path = hash.substringBefore('?').removePrefix("#").trimEnd('/')
    val params = hash.substringAfter('?', "").split('&').filter(String::isNotBlank)
    val orgParams = params.filter { it.substringBefore('=') == "org" }
    val queryOrg = orgParams.singleOrNull()?.substringAfter('=', "")
    val scoped = path == "/org" || path.startsWith("/org/")
    val parts = path.split('/')
    val pathOrg = if (scoped) parts.getOrNull(2) else null
    val invalid = orgParams.size > 1 || (queryOrg != null && !orgRouteUuid.matches(queryOrg)) ||
        (scoped && (pathOrg == null || !orgRouteUuid.matches(pathOrg))) ||
        (pathOrg != null && queryOrg != null && !pathOrg.equals(queryOrg, ignoreCase = true))
    if (invalid) return OrganizationRoute(null, "#/dashboard", invalid = true)
    val org = (pathOrg ?: queryOrg)?.lowercase()
    val resource = if (scoped) "#/" + parts.drop(3).joinToString("/").ifBlank { "dashboard" } else "#" + path.ifBlank { "/" }
    val query = params.filterNot { it.substringBefore('=') == "org" }.joinToString("&")
    return OrganizationRoute(org, resource + if (query.isEmpty()) "" else "?$query")
}

fun organizationRouteHash(orgId: String, resourceHash: String): String {
    require(orgRouteUuid.matches(orgId))
    val resource = parseOrganizationRoute(resourceHash).resourceHash
    require(parseAppRoute(resource).navItem != null)
    return "#/org/${orgId.lowercase()}/" + resource.removePrefix("#/")
}

/** Only our own validated workspace routes can survive an auth-provider callback. */
fun organizationReturnHash(hash: String, search: String): String? {
    if (hash.startsWith("#/") && hash != "#/signin") return null
    if (hasGiverCallback(search) || hasRecoveryRouteHint(hash, search) ||
        search.removePrefix("?").split('&').any { it == "recovery=1" || it.startsWith("invite=") }) return null
    val encoded = search.removePrefix("?").split('&').filter { it.startsWith("return_to=") }.singleOrNull()?.substringAfter('=') ?: return null
    val decoded = runCatching { decodeURIComponent(encoded) }.getOrNull() ?: return null
    if (!decoded.startsWith("#/org/")) return null
    val parsed = parseOrganizationRoute(decoded)
    if (parsed.invalid || parsed.orgId == null || parseAppRoute(parsed.resourceHash).navItem == null) return null
    return organizationRouteHash(parsed.orgId, parsed.resourceHash)
}

fun organizationCallbackUrl(baseUrl: String, hash: String, search: String = ""): String? {
    val target = organizationReturnHash(hash, search) ?: hash
    val parsed = parseOrganizationRoute(target)
    if (parsed.invalid || parsed.orgId == null || parseAppRoute(parsed.resourceHash).navItem == null) return null
    return "$baseUrl?return_to=" + encodeURIComponent(organizationRouteHash(parsed.orgId, parsed.resourceHash))
}

private external fun encodeURIComponent(value: String): String
private external fun decodeURIComponent(value: String): String

/** Preserve workspace identity for page-local navigation and URL-only filter updates. */
fun workspaceHash(resourceHash: String, orgId: String? = parseOrganizationRoute(kotlinx.browser.window.location.hash).orgId): String =
    if (orgId == null) resourceHash else organizationRouteHash(orgId, resourceHash)
